import React, { useState, useContext, useEffect } from "react";
import {
  Dialog, DialogActions, Button, TextField, Box, Typography,
  IconButton, CircularProgress, InputAdornment,
} from "@material-ui/core";
import { Close, OpenInNew, Visibility, VisibilityOff, CheckCircle } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(() => ({
  dialogPaper: { borderRadius: 16, maxWidth: 540, width: "100%" },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "18px 22px 12px", borderBottom: "1px solid #f0f0f0",
  },
  titleText: { fontWeight: 700, fontSize: 18 },
  subtitle: { fontSize: 12, color: "#6b7280", marginTop: 2 },
  content: { padding: "18px 22px 8px", backgroundColor: "#fafafa" },
  section: {
    backgroundColor: "#fff", borderRadius: 10, padding: 16,
    marginBottom: 14, border: "1px solid #e5e7eb",
  },
  sectionTitle: {
    fontWeight: 600, fontSize: 13, color: "#111827",
    display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
  },
  badge: {
    width: 20, height: 20, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0,
  },
  caption: { fontSize: 11, color: "#6b7280", lineHeight: 1.6 },
  actions: { padding: "12px 22px", backgroundColor: "#fafafa", borderTop: "1px solid #f0f0f0" },
  connectBtn: { fontWeight: 600, textTransform: "none", borderRadius: 8 },
  configuredBox: {
    backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0",
    borderRadius: 10, padding: "12px 16px", marginBottom: 14,
    display: "flex", alignItems: "center", gap: 8,
  },
}));

const COLOR = "#0866FF";

const MetaAdsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [configured, setConfigured] = useState(null);
  const [adAccountId, setAdAccountId] = useState("");
  const [form, setForm] = useState({ accessToken: "", adAccountId: "" });
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ accessToken: "", adAccountId: "" });
    api.get("/ads/meta/config").then(({ data }) => {
      setConfigured(data.configured);
      if (data.configured) setAdAccountId(data.adAccountId || "");
    }).catch(() => setConfigured(false));
  }, [open]);

  const setField = (key) => (e) => { const v = e.target.value; setForm(f => ({ ...f, [key]: v })); };

  const handleSave = async () => {
    if (!form.accessToken.trim() || !form.adAccountId.trim()) {
      toast.error("Preencha todos os campos");
      return;
    }
    setSaving(true);
    try {
      await api.post("/ads/meta/config", {
        accessToken: form.accessToken.trim(),
        adAccountId: form.adAccountId.trim(),
      });
      toast.success("Meta Ads conectado com sucesso!");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete("/ads/meta/config");
      setConfigured(false);
      setAdAccountId("");
      toast.success("Integração removida");
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ className: classes.dialogPaper }}>
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.titleText}>
            <span style={{ marginRight: 8 }}>📘</span>Configurar Meta Ads
          </Typography>
          <Typography className={classes.subtitle}>
            Métricas de campanhas do Facebook Ads e Instagram Ads
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><Close /></IconButton>
      </Box>

      <Box className={classes.content}>
        {configured && (
          <Box className={classes.configuredBox}>
            <CheckCircle style={{ color: "#16a34a", fontSize: 20 }} />
            <Box>
              <Typography style={{ fontWeight: 600, fontSize: 13, color: "#15803d" }}>
                Já configurado — Conta: act_{adAccountId}
              </Typography>
              <Typography style={{ fontSize: 11, color: "#166534" }}>
                Preencha os campos abaixo para atualizar as credenciais
              </Typography>
            </Box>
          </Box>
        )}

        {/* Step 1 */}
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <Box className={classes.badge} style={{ backgroundColor: COLOR }}>1</Box>
            Obter Access Token
          </Box>
          <Typography className={classes.caption}>
            1. Acesse <strong>business.facebook.com</strong> → Configurações do Business<br />
            2. Vá em <strong>Usuários → Usuários de Sistema</strong><br />
            3. Crie um usuário de sistema (ou use um existente)<br />
            4. Clique em <strong>Gerar Token</strong> e selecione o App<br />
            5. Marque a permissão <strong>ads_read</strong> e gere o token
          </Typography>
          <Button
            size="small" variant="outlined" startIcon={<OpenInNew style={{ fontSize: 14 }} />}
            style={{ marginTop: 8, textTransform: "none", fontSize: 12 }}
            onClick={() => window.open("https://business.facebook.com/settings/system-users", "_blank")}
          >
            Abrir Business Manager
          </Button>
        </Box>

        {/* Step 2 */}
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <Box className={classes.badge} style={{ backgroundColor: COLOR }}>2</Box>
            Obter Ad Account ID
          </Box>
          <Typography className={classes.caption}>
            No <strong>Meta Ads Manager</strong>, o ID da conta aparece na URL ou no menu do topo da página no formato <strong>act_123456789</strong>. Você pode usar só os números (sem "act_").
          </Typography>
          <Button
            size="small" variant="outlined" startIcon={<OpenInNew style={{ fontSize: 14 }} />}
            style={{ marginTop: 8, textTransform: "none", fontSize: 12 }}
            onClick={() => window.open("https://adsmanager.facebook.com", "_blank")}
          >
            Abrir Ads Manager
          </Button>
        </Box>

        {/* Step 3 — Credenciais */}
        <Box className={classes.section}>
          <Box className={classes.sectionTitle}>
            <Box className={classes.badge} style={{ backgroundColor: COLOR }}>3</Box>
            Inserir credenciais
          </Box>

          <TextField
            label="Access Token"
            variant="outlined"
            fullWidth
            size="small"
            type={showToken ? "text" : "password"}
            value={form.accessToken}
            onChange={setField("accessToken")}
            style={{ marginBottom: 12 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setShowToken(v => !v)}>
                    {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Ad Account ID (ex: 123456789)"
            variant="outlined"
            fullWidth
            size="small"
            value={form.adAccountId}
            onChange={setField("adAccountId")}
          />
        </Box>
      </Box>

      <DialogActions className={classes.actions}>
        {configured && (
          <Button onClick={handleRemove} style={{ color: "#ef4444", textTransform: "none" }}>
            Remover integração
          </Button>
        )}
        <Box flex={1} />
        <Button onClick={onClose} style={{ textTransform: "none" }}>Fechar</Button>
        <Button
          variant="contained"
          className={classes.connectBtn}
          style={{ backgroundColor: COLOR, color: "#fff" }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Salvar configuração"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetaAdsModal;
